import type { User } from '@supabase/supabase-js';
import { supabase } from '../../../lib/supabase';
import type { Organization } from '../../../lib/auth-context';

type BrandingRecord = {
  company_name?: string | null;
  phone?: string | null;
  address?: string | null;
  city?: string | null;
  province?: string | null;
  postal_code?: string | null;
  country_code?: string | null;
};

const EMPLOYEE_SCOPE_COLUMNS = ['org_id', 'tenant_id'] as const;

function canAutoProvisionEmployee(org: Organization | null): boolean {
  if (!org) return false;
  const role = String(org.role || '').toLowerCase();
  return org.is_owner || role === 'admin' || role === 'owner';
}

function isMissingColumnError(error: { message?: string; details?: string; hint?: string; code?: string } | null) {
  if (!error) return false;
  const haystack = [error.message, error.details, error.hint, error.code].filter(Boolean).join(' ').toLowerCase();
  return haystack.includes('column') && haystack.includes('does not exist');
}

function parseName(user: User) {
  const meta = (user.user_metadata || {}) as Record<string, unknown>;
  const fullName = String(meta.full_name || meta.name || '').trim();
  const explicitFirst = String(meta.first_name || '').trim();
  const explicitLast = String(meta.last_name || '').trim();

  if (explicitFirst || explicitLast) {
    return {
      firstName: explicitFirst || 'Admin',
      lastName: explicitLast || 'User',
    };
  }

  if (fullName) {
    const parts = fullName.split(/\s+/);
    return {
      firstName: parts[0] || 'Admin',
      lastName: parts.slice(1).join(' ') || 'User',
    };
  }

  const emailPrefix = String(user.email || 'admin').split('@')[0];
  const parts = emailPrefix.split(/[._-]+/).filter(Boolean);
  return {
    firstName: parts[0] ? parts[0][0].toUpperCase() + parts[0].slice(1) : 'Admin',
    lastName: parts[1] ? parts[1][0].toUpperCase() + parts[1].slice(1) : 'User',
  };
}

async function findScopedEmployee(tenantId: string, email: string) {
  for (const scopeColumn of EMPLOYEE_SCOPE_COLUMNS) {
    const { data, error } = await supabase
      .from('employees')
      .select('id')
      .eq(scopeColumn, tenantId)
      .eq('email', email)
      .limit(1);

    if (error) {
      if (isMissingColumnError(error)) continue;
      throw error;
    }

    if (data && data.length > 0) {
      return data[0];
    }
  }

  return null;
}

async function loadBranding(tenantId: string): Promise<BrandingRecord | null> {
  const { data, error } = await supabase
    .from('branding')
    .select('company_name, phone, address, city, province, postal_code, country_code')
    .eq('tenant_id', tenantId)
    .maybeSingle();

  if (error) return null;
  return data as BrandingRecord | null;
}

async function insertEmployeeWithFallback(tenantId: string, payload: Record<string, unknown>) {
  const attempts = [
    { org_id: tenantId, ...payload },
    { tenant_id: tenantId, ...payload },
    {
      org_id: tenantId,
      employee_number: payload.employee_number,
      first_name: payload.first_name,
      last_name: payload.last_name,
      email: payload.email,
      department: payload.department,
      position: payload.position,
      hire_date: payload.hire_date,
    },
    {
      tenant_id: tenantId,
      employee_number: payload.employee_number,
      first_name: payload.first_name,
      last_name: payload.last_name,
      email: payload.email,
      department: payload.department,
      position: payload.position,
      hire_date: payload.hire_date,
    },
  ];

  for (const attempt of attempts) {
    const { error } = await supabase.from('employees').insert(attempt);
    if (!error) return;
    if (isMissingColumnError(error)) continue;
    if (String(error.message || '').toLowerCase().includes('duplicate')) return;
    throw error;
  }
}

export async function ensureAdminEmployeeRecord(
  user: User | null,
  currentOrg: Organization | null,
  tenantId: string | null
): Promise<void> {
  if (!user?.email || !tenantId || !canAutoProvisionEmployee(currentOrg)) return;

  const existing = await findScopedEmployee(tenantId, user.email);
  if (existing) return;

  const branding = await loadBranding(tenantId);
  const names = parseName(user);
  const adminRole = currentOrg?.is_owner ? 'Owner' : 'Administrator';
  const companyPrefix = (branding?.company_name || currentOrg?.name || 'ORG')
    .replace(/[^A-Za-z0-9]/g, '')
    .slice(0, 6)
    .toUpperCase();

  await insertEmployeeWithFallback(tenantId, {
    employee_number: `${companyPrefix || 'ADMIN'}-001`,
    first_name: names.firstName,
    last_name: names.lastName,
    email: user.email,
    department: 'Management',
    position: adminRole,
    hire_date: new Date().toISOString().slice(0, 10),
    phone: branding?.phone || null,
    address: branding?.address || null,
    city: branding?.city || null,
    province_state: branding?.province || null,
    postal_code: branding?.postal_code || null,
    country: branding?.country_code || null,
    is_active: true,
    pay_type: 'salaried',
  });
}
